import type { AppAbilities } from '@app/icare/shared/auth/ability'
import { appAbilityFactory } from '@app/icare/shared/auth/ability'
import { deriveCurrentUser } from '@app/icare/shared/auth/currentUser'
import type { BetterFetchOption } from '@better-fetch/fetch'
import type { CanParameters } from '@casl/ability'
import { canFactory } from '@pkg/auth/ability'
import { appError } from '@pkg/shared/errors'
import { adminClient, anonymousClient, twoFactorClient } from 'better-auth/client/plugins'
import { createAuthClient, useStore } from 'better-auth/react'
import { computed } from 'nanostores'
import { useMemo } from 'react'

export const authClientFactory = (options?: BetterFetchOption) =>
  createAuthClient({
    basePath: '/api/auth',
    fetchOptions: {
      ...options,
      throw: true,
    },
    plugins: [adminClient(), anonymousClient(), twoFactorClient()],
  })

export const authClient = authClientFactory()

export type AppSession = typeof authClient.$Infer.Session
export type AppAuth = typeof authClient

const sessionAtom = authClient.$store.atoms.session
if (!sessionAtom) {
  throw appError('INTERNAL', `Authclient session not avaialble`)
}

// CASL ability and session are one reactive graph: the ability is a pure
// derivation of the session atom and can never be stale relative to it.
export const abilityAtom = computed(sessionAtom, value =>
  appAbilityFactory({ session: (value.data ?? undefined) as AppSession | undefined }),
)

const STORAGE_KEY = 'auth.session'

interface SessionAtomValue {
  data: AppSession | null
  isPending: boolean
  isRefetching: boolean
}

// Offline-ready better-auth: seed the in-memory session atom from localStorage
// on boot, then persist every settled change back. The boot fetch reconciles
// online; offline it errors and keeps the seeded value (better-auth/query.mjs).
if (typeof window !== 'undefined') {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const data = JSON.parse(raw) as AppSession
      sessionAtom.set({ ...sessionAtom.get(), data, isPending: false, error: null })
    }
  } catch {
    localStorage.removeItem(STORAGE_KEY)
  }

  authClient.$store.listen('session', (value: SessionAtomValue) => {
    if (value.data) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value.data))
    } else if (!value.isPending && !value.isRefetching) {
      localStorage.removeItem(STORAGE_KEY)
    }
  })
}

type CanAction = CanParameters<AppAbilities>[0]
type CanSubject = CanParameters<AppAbilities>[1]

export const useAuth = () => {
  const value = useStore(sessionAtom)
  const ability = useStore(abilityAtom)
  const session = (value.data ?? undefined) as AppSession | undefined
  const can = canFactory<typeof ability, AppAbilities>(ability)
  return {
    session,
    current: deriveCurrentUser(session),
    status: value.isPending && !value.data ? 'unknown' : value.data ? 'authenticated' : 'unauthenticated',
    ability,
    can,
    allowed: (action: CanAction, subject: CanSubject, field?: string) => can(action, subject, field).can,
  }
}

export const useAbility = () => useStore(abilityAtom)

export const useCan = (action: CanAction, subject: CanSubject, field?: string, debug?: boolean) => {
  const ability = useAbility()
  return useMemo(() => ability.can(action, subject, field, debug), [ability, action, subject, field, debug])
}

export const getCurrentUser = () => deriveCurrentUser((sessionAtom.get().data ?? undefined) as AppSession | undefined)

export const getLocalSession = () => (sessionAtom.get().data ?? undefined) as AppSession | undefined

export { signInWithMicrosoft, signOut } from './auth/auth'
